import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ConfirmModal from './ConfirmModal';
import HistoryTimeline from './HistoryTimeline';
import TagsSelect from './TagsSelect';
import utils from './utils';
import { loadTags } from '../slices/thunk';
import { APIClient } from "../helpers/api_helper";
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Drawer, Form,
  Input, Row, Segmented, Space
} from 'antd';

const api = new APIClient();


const TagsDrawer = (props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [activeDrawerTab, setActiveDrawerTab] = useState('data');
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [data, setData] = useState(null);

  const tagsList = useSelector(state => state.Tags.list);
  const tagsById = useSelector(state => state.Tags.byId);

  // Which tags may NOT be picked as the parent of the tag being edited:
  // itself, any of its descendants, or a tag so deep that nesting this
  // tag's subtree under it would exceed the depth limit.
  const editedHeight = props.id ? utils.tagSubtreeHeight(tagsList, props.id) : 1;
  const descendantIds = props.id ? utils.tagDescendantIds(tagsList, props.id) : new Set();
  const excludeIds = tagsList
    .filter(t =>
      (props.id && t.id === props.id) ||
      descendantIds.has(t.id) ||
      utils.tagDepth(tagsById, t.id) + editedHeight > utils.TAG_MAX_DEPTH
    )
    .map(t => t.id);

  useEffect(() => {
    setActiveDrawerTab('data');
    if (props.id) {
      try {
        api.get(`/api/tags/${props.id}/`, {})
          .then(resp => {
            setData(resp);
            form.setFieldsValue(resp);
          });
      } catch (error) {
        console.error(error);
      }
    } else {
      form.resetFields();
      setData(null);
    }
  }, [props.isOpen, props.id]);


  const handleCancel = () => {
    form.resetFields();
    props.onCancel()
  }

  const handleConfirmedDelete = () => {
    api.delete(`/api/tags/${props.id}/`, {}, {successMsg: `Uspešno izbrisana oznaka ${props.id}: ${data.title}`})
      .then(resp => {
        dispatch(loadTags());  // refresh app-wide hierarchy
        props.onDone()
        form.resetFields();
        setShowDeletionConfirmModal(false);
      })
      .catch(error => {
        console.error(error)
      })
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const isEdit = props.id !== null;
        const apiCall = isEdit
            ? api.update(`/api/tags/${props.id}/`, values)
            : api.create('/api/tags/', values, {successMsg: `Uspešno ustvarjena oznaka ${values.title}`});

        apiCall.then(resp => {
            dispatch(loadTags());  // refresh app-wide hierarchy
            props.onDone();
            form.resetFields();
          }).catch(err => {
            const respData = err?.response?.data;
            if (respData && typeof respData === 'object' && !Array.isArray(respData)) {
              form.setFields(
                Object.entries(respData).map(([name, errors]) => ({
                  name,
                  errors: Array.isArray(errors) ? errors : [String(errors)],
                }))
              );
            }
            console.error('API error:', err);
          });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  const isCreation = props.id == null;

  return (
    <>
      <Form layout="vertical" form={form}>
        <Drawer
          title={
            <Space>
              <span>{isCreation ? "Ustvari novo oznako" : `Oznaka #${props.id}`}</span>
              {!isCreation && (
                <Segmented
                  size="small"
                  value={activeDrawerTab}
                  onChange={setActiveDrawerTab}
                  options={[
                    { label: 'Podatki', value: 'data' },
                    { label: 'Zgodovina', value: 'history' },
                  ]}
                />
              )}
            </Space>
          }
          width={720}
          onClose={handleCancel}
          open={props.open}
          extra={
            activeDrawerTab === 'data' && (
              <Space>
                <Button disabled={isCreation} onClick={() => setShowDeletionConfirmModal(true)} danger><DeleteOutlined /></Button>
                <Button onClick={handleCancel}><CloseOutlined /></Button>
                <Button onClick={onSubmit} type="primary">Shrani</Button>
              </Space>
            )
          }
        >
          {activeDrawerTab === 'data' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="title" label='Naziv' rules={[]}>
                  <Input placeholder={'naziv...'} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="parent" label='Nadrejena oznaka'>
                  <TagsSelect
                    multiple={false}
                    allowCreate={false}
                    minWidth={0}
                    placeholder={'Brez (oznaka najvišjega nivoja)'}
                    excludeIds={excludeIds}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {activeDrawerTab === 'history' && (
            <HistoryTimeline model="Tag" objectId={props.id} />
          )}
        </Drawer>
      </Form>

      <ConfirmModal
        textComponent={<p>{`Ste prepričani, da želite izbrisati oznako #${data?.id}: ${data?.title}?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{'danger': true}}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => {
          setShowDeletionConfirmModal(false);
        }}
      />
    </>
  );
};

export default TagsDrawer;
